import React from 'react';
import { multiFactor } from 'firebase/auth';
import { useLoaderData } from 'react-router';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import WifiOffOutlinedIcon from '@mui/icons-material/WifiOffOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Button, Typography, Box, Stack, Fade } from '@mui/material';

import { useAuth } from '../../../../hooks/useAuth';
import { USER_TYPES } from '../../../../utils/constants';
import { MFA_FACTOR_IDS } from '../../constants';
import { isUserEnrolledInMFAMethod } from '../../utils';
import { AuthenticationWrapper } from '../../AuthenticationWrapper';
import { mfaColors, mfaCardStyles, mfaPrimaryButtonStyles } from '../styles';
import { SecurityRequirementHeader } from './SecurityRequirementHeader';

const SECURITY_BENEFITS = [
  {
    icon: ShieldOutlinedIcon,
    title: 'Stronger Protection',
    description: "Codes generated on your device can't be intercepted like an SMS message.",
    color: '#10b981',
    bgColor: '#d1fae5',
  },

  {
    icon: BoltOutlinedIcon,
    title: 'Instant & Reliable',
    description: 'Codes appear instantly. No more waiting for a delayed SMS message.',
    color: '#f59e0b',
    bgColor: '#fef3c7',
  },
  {
    icon: VerifiedUserOutlinedIcon,
    title: 'Phishing Resistant',
    description: 'Protects against SIM-swapping and phone number hijacking.',
    color: '#8b5cf6',
    bgColor: '#ede9fe',
  },
  {
    icon: WifiOffOutlinedIcon,
    title: 'Works Without Signal',
    description: 'Generate codes anytime! No cell reception or internet needed.',
    color: '#3b82f6',
    bgColor: '#dbeafe',
  },
];

const BenefitCard = ({ icon: Icon, title, description, color, bgColor }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2.5,
      p: 2,
      borderRadius: 2,
      backgroundColor: '#fff',
      border: `1px solid ${mfaColors.border}`,
      transition: 'border-color 0.2s ease',
      '&:hover': {
        borderColor: color,
      },
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 1.5,
        backgroundColor: bgColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon sx={{ color, fontSize: 22 }} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: mfaColors.text, mb: 0.25 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: mfaColors.textMuted, lineHeight: 1.5 }}>
        {description}
      </Typography>
    </Box>
  </Box>
);

const upgradeBoxStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  padding: '2px',
  border: '1px solid #fed7aa',
  borderRadius: 2,
  backgroundColor: '#fff7ed',
};

const Forced2FAUpgradeNotice = ({ hasPhoneMFA = true }) => {
  if (!hasPhoneMFA) {
    return null;
  }
  // Only show this notice if the user has SMS 2FA enrolled and must use the Authenticator App
  // for 2FA going forward. This should only appear for staff members who've not yet upgraded.
  return (
    <Box sx={upgradeBoxStyles}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <PhoneAndroidIcon sx={{ color: '#ea580c', fontSize: 20 }} />
        <ArrowForwardIcon sx={{ color: '#9a3412', fontSize: 16 }} />
        <LockOutlinedIcon sx={{ color: mfaColors.primary, fontSize: 20 }} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ color: '#9a3412', fontWeight: 600 }}>
          Upgrading from SMS
        </Typography>
        <Typography variant="caption" sx={{ color: '#c2410c', lineHeight: 1.4, display: 'block' }}>
          Your SMS authentication will be replaced with the more secure authenticator app.
        </Typography>
      </Box>
    </Box>
  );
};
export const BenefitsSection = () => (
  <Box>
    <Typography
      sx={{
        mb: 2,
        color: mfaColors.primary,
        fontWeight: 700,
        fontSize: '0.8rem',
        display: 'block',
        letterSpacing: '0.1em',
      }}
    >
      Why use an authenticator app
    </Typography>

    <Stack spacing={1.5}>
      {SECURITY_BENEFITS.map((benefit) => (
        <BenefitCard
          key={benefit.title}
          icon={benefit.icon}
          title={benefit.title}
          description={benefit.description}
          color={benefit.color}
          bgColor={benefit.bgColor}
        />
      ))}
    </Stack>
  </Box>
);

const CTAButton = ({ hasPhoneMFA, onContinue }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
    <Button
      variant="contained"
      onClick={onContinue}
      endIcon={<ArrowForwardIcon />}
      sx={{
        ...mfaPrimaryButtonStyles,
        px: 5,
        py: 1.5,
        fontSize: '1rem',
      }}
    >
      {hasPhoneMFA ? 'Continue to upgrade' : 'Get started'}
    </Button>
  </Box>
);

export const SecurityRequirementPage = ({ onContinue }) => {
  const { user } = useLoaderData();
  const { userType } = useAuth();
  const multiFactorUser = multiFactor(user);
  const hasPhoneMFA = isUserEnrolledInMFAMethod({ factorId: MFA_FACTOR_IDS.PHONE, multiFactorUser });
  const isPatient = userType === USER_TYPES.PATIENT;

  const headline = hasPhoneMFA ? 'Upgrade Your Security' : 'Security Update Required';

  return (
    <AuthenticationWrapper>
      <Fade in>
        <Box
          sx={{
            ...mfaCardStyles,
            my: 2,
            width: '100%',
            maxWidth: 520,
            p: { xs: 3, sm: 4 },
          }}
        >
          <Stack spacing={3}>
            <SecurityRequirementHeader headline={headline} isPatient={isPatient} />

            <Forced2FAUpgradeNotice hasPhoneMFA={hasPhoneMFA} />

            <CTAButton hasPhoneMFA={hasPhoneMFA} onContinue={onContinue} />
            <BenefitsSection />
          </Stack>
        </Box>
      </Fade>
    </AuthenticationWrapper>
  );
};
